import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { useSessionJwt } from '../hooks/useSessionJwt';
import { Hash, Layers, ExternalLink, MessageSquare, X } from 'lucide-react';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useTimeRange } from '../contexts/TimeRangeContext';
import { palette, typography } from '../utils/designTokens';
import { formatDuration, formatTokens, formatCost } from '../utils/formatters';
import { enhanceSessionsResponse, enhanceTracesResponse, isInDemoMode } from '../services/demoDataService';
import { v2ServicePath } from '../config';
import { normalizeV1Trace, normalizeV2Trace } from '../services/tracesApi';
import { useNavigateWithRange } from '../hooks/useNavigateWithRange';
import ChatTurn from './ChatTurn';
import CopyButton from './CopyButton';
import ViewTabs from './ViewTabs';
import { SummaryPill } from './TraceSummaryBar';
import SessionAgentGraph from '../pages/trace-detail/SessionAgentGraph';
import TraceViewer from '../pages/trace-detail/TraceViewer';

const TRACE_LIMIT = 200;

const SectionTitle = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-center gap-2">
    {Icon && <Icon size={14} style={{ color: palette.text.muted }} />}
    <div>
      <div className={`${typography.size.sm} ${typography.weight.semibold}`} style={{ color: palette.text.primary }}>
        {title}
      </div>
      {subtitle && (
        <div className={typography.size.xs} style={{ color: palette.text.muted }}>
          {subtitle}
        </div>
      )}
    </div>
  </div>
);


export default function SessionViewer({
  appId,
  sessionId,
  initialSessionSummary = null,
  variant = 'page',
  initialTraceId = null,
  onClose = null,
  showOpenTraces = true,
  className = ''
}) {
  const { selectedAppId } = useParams();
  const resolvedAppId = appId || selectedAppId;

  if (!resolvedAppId) {
    throw new Error('SessionViewer requires appId prop or a route param');
  }
  if (!sessionId) {
    throw new Error('SessionViewer requires sessionId prop');
  }

  const navigateWithRange = useNavigateWithRange();
  const { selectedEnvironment } = useEnvironment();
  const { selectedRange } = useTimeRange();
  const { session } = useStytchMemberSession();
  const { sessionId: authSessionId, getJwt } = useSessionJwt();
  const tenantId = session?.organization_id || null;
  const isDemo = isInDemoMode(tenantId, selectedEnvironment, resolvedAppId);

  const navigationSessionSummary = useMemo(() => {
    if (!initialSessionSummary) return null;
    if (initialSessionSummary.session_id !== sessionId) return null;
    return initialSessionSummary;
  }, [initialSessionSummary, sessionId]);

  const [sessionSummary, setSessionSummary] = useState(navigationSessionSummary);
  const [sessionLoading, setSessionLoading] = useState(!navigationSessionSummary);
  const [sessionError, setSessionError] = useState(null);

  const [traces, setTraces] = useState([]);
  const [tracesLoading, setTracesLoading] = useState(true);
  const [tracesError, setTracesError] = useState(null);
  const [hasMoreTraces, setHasMoreTraces] = useState(false);

  const [selectedTraceId, setSelectedTraceId] = useState(initialTraceId);
  const [activeTab, setActiveTab] = useState('outline');
  const [sessionView, setSessionView] = useState('conversation'); // 'conversation' | 'agents'
  const [turnSignalsPortalTarget, setTurnSignalsPortalTarget] = useState(null);

  // participant_count per trace_id — fetched from v2 session detail.
  const [participantCounts, setParticipantCounts] = useState({});

  // Chat strip (conversation panel) resize state
  const [chatStripWidth, setChatStripWidth] = useState(475);
  const [isChatDragging, setIsChatDragging] = useState(false);
  const isChatDraggingRef = useRef(false);

  const shouldSkipInitialSessionFetchRef = useRef(Boolean(navigationSessionSummary));
  const lastTracesRequestKeyRef = useRef(null);

  const sortedTraces = useMemo(() => {
    if (!traces) return [];
    return [...traces].sort((a, b) => {
      const aTime = a.trace_start ? new Date(a.trace_start).getTime() : 0;
      const bTime = b.trace_start ? new Date(b.trace_start).getTime() : 0;
      return aTime - bTime;
    });
  }, [traces]);

  const selectedTrace = useMemo(() => {
    if (!selectedTraceId) return null;
    return sortedTraces.find(trace => trace.trace_id === selectedTraceId) || null;
  }, [selectedTraceId, sortedTraces]);

  const selectedTraceUrl = useMemo(() => {
    if (!selectedTraceId) return '';
    const params = new URLSearchParams();
    if (selectedEnvironment) params.set('env', selectedEnvironment);
    if (selectedRange) params.set('range', selectedRange);
    const query = params.toString();
    return `/app/${resolvedAppId}/traces/${encodeURIComponent(selectedTraceId)}${query ? `?${query}` : ''}`;
  }, [resolvedAppId, selectedEnvironment, selectedRange, selectedTraceId]);

  const tracesRequestKey = useMemo(
    () => [resolvedAppId, sessionId, selectedEnvironment || '', selectedRange || '', authSessionId || ''].join('|'),
    [resolvedAppId, sessionId, selectedEnvironment, selectedRange, authSessionId]
  );

  useEffect(() => {
    const hasNavigationSummary = Boolean(navigationSessionSummary);
    shouldSkipInitialSessionFetchRef.current = hasNavigationSummary;

    if (hasNavigationSummary) {
      setSessionSummary(navigationSessionSummary);
      setSessionError(null);
      setSessionLoading(false);
      return;
    }

    setSessionSummary(null);
    setSessionLoading(true);
  }, [navigationSessionSummary, sessionId]);

  // Clear selection if selected trace no longer exists in data
  useEffect(() => {
    if (!selectedTraceId || sortedTraces.length === 0) return;
    const exists = sortedTraces.some(trace => trace.trace_id === selectedTraceId);
    if (!exists) setSelectedTraceId(null);
  }, [sortedTraces, selectedTraceId]);

  // Chat strip resize handlers
  const handleChatMouseMove = useCallback((e) => {
    if (!isChatDraggingRef.current) return;
    const container = document.querySelector('.session-main-layout');
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const newWidth = e.clientX - containerRect.left;
    setChatStripWidth(Math.min(Math.max(newWidth, 200), 600));
  }, []);

  const handleChatMouseUp = useCallback(() => {
    isChatDraggingRef.current = false;
    setIsChatDragging(false);
  }, []);

  const handleChatMouseDown = useCallback(() => {
    isChatDraggingRef.current = true;
    setIsChatDragging(true);
  }, []);

  useEffect(() => {
    if (isChatDragging) {
      document.addEventListener('mousemove', handleChatMouseMove);
      document.addEventListener('mouseup', handleChatMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleChatMouseMove);
        document.removeEventListener('mouseup', handleChatMouseUp);
      };
    }
  }, [isChatDragging, handleChatMouseMove, handleChatMouseUp]);

  const fetchSessionDetail = useCallback(async () => {
    const jwt = getJwt();
    if (!jwt) return;

    const abortController = new AbortController();
    try {
      setSessionLoading(true);
      setSessionError(null);

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const params = new URLSearchParams();
      if (selectedEnvironment) params.append('env', selectedEnvironment);
      if (selectedRange) params.append('range', selectedRange);

      const response = await fetch(`${apiUrl}${v2ServicePath(resolvedAppId, `/sessions/${encodeURIComponent(sessionId)}`)}?${params.toString()}`, {
        signal: abortController.signal,
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok && !isDemo) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const rawData = response.ok ? await response.json() : {};
      if (isDemo) {
        const data = enhanceSessionsResponse(rawData, tenantId, selectedEnvironment, resolvedAppId);
        const sessions = data.sessions || [];
        setSessionSummary(sessions.find(s => s.session_id === sessionId) || null);
      } else {
        setSessionSummary(rawData.data || null);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setSessionError(err.message);
    } finally {
      setSessionLoading(false);
    }

    return () => abortController.abort();
  }, [authSessionId, getJwt, selectedEnvironment, selectedRange, resolvedAppId, sessionId, isDemo, tenantId]);

  const fetchTraces = useCallback(async () => {
    const jwt = getJwt();
    if (!jwt) return;

    const abortController = new AbortController();
    try {
      setTracesLoading(true);
      setTracesError(null);

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const params = new URLSearchParams();
      params.append('session_id', sessionId);
      params.append('limit', TRACE_LIMIT.toString());

      params.append('env', selectedEnvironment || '');
      params.append('range', selectedRange || '');

      const endpoint = `${apiUrl}${v2ServicePath(resolvedAppId, '/traces')}?${params.toString()}`;

      const response = await fetch(endpoint, {
        signal: abortController.signal,
        headers: {
          Authorization: `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok && !isDemo) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const rawData = response.ok ? await response.json() : {};
      const data = isDemo
        ? enhanceTracesResponse(rawData, tenantId, selectedEnvironment, resolvedAppId)
        : rawData;

      const rows = isDemo
        ? (Array.isArray(data.traces) ? data.traces.map(normalizeV1Trace) : [])
        : (Array.isArray(data.data) ? data.data.map(normalizeV2Trace) : []);
      setTraces(rows);
      setHasMoreTraces(Boolean(isDemo ? data.pagination?.has_more : data.meta?.has_more));
    } catch (err) {
      if (err.name === 'AbortError') return;
      setTracesError(err.message);
    } finally {
      setTracesLoading(false);
    }

    return () => abortController.abort();
  }, [authSessionId, getJwt, selectedEnvironment, selectedRange, resolvedAppId, sessionId, isDemo, tenantId]);

  useEffect(() => {
    if (shouldSkipInitialSessionFetchRef.current) {
      shouldSkipInitialSessionFetchRef.current = false;
      return;
    }
    fetchSessionDetail();
  }, [fetchSessionDetail]);

  useEffect(() => {
    if (!getJwt()) return;
    if (lastTracesRequestKeyRef.current === tracesRequestKey) return;
    lastTracesRequestKeyRef.current = tracesRequestKey;
    fetchTraces();
  }, [fetchTraces, tracesRequestKey, getJwt]);

  useEffect(() => {
    setParticipantCounts({});
    const jwt = getJwt();
    if (!jwt || isDemo) return;
    let cancelled = false;
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    (async () => {
      try {
        const url = `${apiUrl}${v2ServicePath(resolvedAppId, `/sessions/${encodeURIComponent(sessionId)}`)}?env=${selectedEnvironment || ''}`;
        const resp = await fetch(url, { headers: { Authorization: `Bearer ${jwt}` } });
        if (cancelled || !resp.ok) return;
        const body = await resp.json();
        const tracesArr = Array.isArray(body?.data?.traces) ? body.data.traces : [];
        const counts = {};
        for (const t of tracesArr) {
          if (t.trace_id) counts[t.trace_id] = t.participant_count ?? 1;
        }
        if (!cancelled) setParticipantCounts(counts);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [authSessionId, getJwt, resolvedAppId, sessionId, selectedEnvironment, isDemo]);

  const containerClass = useMemo(() => {
    const base = 'flex flex-col gap-4';
    if (variant === 'dialog') return `${base} max-h-[80vh]`;
    if (variant === 'tab') return `${base} h-full`;
    return `${base} flex-1 min-h-0`;
  }, [variant]);

  return (
    <div className={`${containerClass} ${className}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Hash size={16} style={{ color: palette.text.muted }} />
            <span className={`${typography.size.xl} ${typography.weight.semibold}`} style={{ color: palette.text.heading }}>
              Session {sessionId.slice(0, 8)}
            </span>
            <CopyButton text={sessionId} size="sm" label="Copy Session ID" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {showOpenTraces && (
            <button
              type="button"
              className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-border-subtle bg-background-tertiary hover:bg-background-quaternary"
              onClick={() => navigateWithRange(`/app/${resolvedAppId}/traces`, { sessionId })}
            >
              <ExternalLink size={12} />
              Open in Traces
            </button>
          )}
          {onClose && (
            <button
              type="button"
              className="text-xs px-2 py-1 rounded border border-border-subtle bg-background-tertiary hover:bg-background-quaternary"
              onClick={onClose}
            >
              Close
            </button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-border-subtle bg-background-secondary p-3">
        <div className="flex flex-wrap gap-3">
          {sessionSummary?.user_id && <SummaryPill label="User" value={sessionSummary.user_id} />}
          <SummaryPill label="Turns" value={sessionSummary?.trace_count ?? sortedTraces.length} />
          <SummaryPill label="Spans" value={sessionSummary?.total_spans ?? '—'} />
          <SummaryPill label="Duration" value={formatDuration(sessionSummary?.total_duration_ms)} />
          <SummaryPill label="Errors" value={sessionSummary?.error_spans ?? 0} />
          <SummaryPill label="Tokens" value={formatTokens(sessionSummary?.total_tokens || 0)} />
          <SummaryPill label="Cost" value={formatCost(sessionSummary?.cost)} />
          {sessionSummary?.has_prompt_injection && (
            <SummaryPill label="Prompt Injection" value="Yes" />
          )}
          {sessionSummary?.has_data_exfiltration && (
            <SummaryPill label="Data Exfiltration" value="Yes" />
          )}
        </div>
        {sessionError && (
          <div className="mt-2 text-xs text-danger">{sessionError}</div>
        )}
        {sessionLoading && (
          <div className="mt-2 text-xs text-text-muted">Loading session metadata...</div>
        )}
      </div>

      {/* Top-level view tabs */}
      <div className="flex items-center gap-1 border-b border-border-subtle pb-2">
        <ViewTabs
          tabs={[
            { value: 'conversation', label: 'Conversation' },
            { value: 'agents', label: 'Business Tasks' },
          ]}
          activeTab={sessionView}
          onTabChange={setSessionView}
        />
      </div>

      {sessionView === 'conversation' && (
        <div className="flex min-h-0 flex-1 session-main-layout">
          {/* Chat Strip */}
          <div
            className="flex flex-col gap-3 min-h-0 shrink-0"
            style={{ width: selectedTraceId ? `${chatStripWidth}px` : '40%' }}
          >
            <SectionTitle
              icon={MessageSquare}
              title="Conversation"
              subtitle={tracesLoading ? 'Loading turns...' : `${sortedTraces.length} turn${sortedTraces.length === 1 ? '' : 's'}`}
            />
            {tracesError && <div className="text-xs text-danger">{tracesError}</div>}
            {!tracesLoading && sortedTraces.length === 0 && (
              <div className="rounded-lg border border-border-subtle bg-background-tertiary p-3 text-xs text-text-muted">
                No turns found for this session.
              </div>
            )}
            <div className="flex-1 min-h-0 flex overflow-auto">
              <div className="flex-1 min-w-0 flex flex-col gap-0">
                {sortedTraces.map(trace => (
                  <ChatTurn
                    key={trace.trace_id}
                    trace={trace}
                    isSelected={trace.trace_id === selectedTraceId}
                    onSelect={setSelectedTraceId}
                    participantCount={participantCounts[trace.trace_id] ?? 1}
                  />
                ))}
                {hasMoreTraces && (
                  <div className="text-xs text-text-muted">
                    Showing first {TRACE_LIMIT} traces. Refine filters to see more.
                  </div>
                )}
              </div>
            </div>
          </div>

          {selectedTraceId && (
            <>
              {/* Chat Strip Resize Handle */}
              <div
                className="w-1 hover:w-2 transition-all cursor-col-resize flex items-center justify-center group mx-1 shrink-0"
                style={{ backgroundColor: isChatDragging ? palette.accents.primary.base : palette.borders.subtle }}
                onMouseDown={handleChatMouseDown}
              >
                <div
                  className="w-0.5 h-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ backgroundColor: palette.accents.primary.base }}
                />
              </div>

              {/* Turn Details */}
              <div className="flex flex-col min-h-0 flex-1 min-w-0 border-l border-border-subtle bg-background-secondary">
                <div className="flex items-center justify-between gap-2 px-2 py-2 border-b border-border-subtle">
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedTraceId(null)}
                      className="p-0.5 rounded hover:bg-background-tertiary"
                      title="Hide turn details"
                    >
                      <Layers size={12} style={{ color: palette.accents.primary.base }} />
                    </button>
                    <span className="text-xs font-semibold text-text-primary">Turn Details</span>
                    {selectedTraceUrl && (
                      <Link
                        to={selectedTraceUrl}
                        className="min-w-0 max-w-[180px] truncate font-mono text-[11px] text-accent-primary-base hover:underline"
                        title={selectedTraceId}
                      >
                        Trace {selectedTraceId?.slice(0, 8)}
                      </Link>
                    )}
                    {selectedTrace && (
                      <ViewTabs
                        tabs={[
                          { value: 'outline', label: 'Outline' },
                          { value: 'timeline', label: 'Timeline' },
                          { value: 'graph', label: 'Graph' },
                          { value: 'agents', label: 'Business Tasks' },
                        ]}
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                      />
                    )}
                    <div
                      ref={setTurnSignalsPortalTarget}
                      className="min-w-[220px] flex-1 empty:hidden"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedTraceId(null)}
                    className="p-1 rounded hover:bg-background-tertiary"
                    title="Close turn details"
                  >
                    <X size={14} style={{ color: palette.text.muted }} />
                  </button>
                </div>

                <div className="flex-1 overflow-hidden flex flex-col p-1.5 gap-1.5">
                  {!selectedTrace ? (
                    <div className="rounded-lg border border-border-subtle bg-background-tertiary p-4 text-sm text-text-muted">
                      Select a turn to see trace details.
                    </div>
                  ) : (
                    <TraceViewer
                      traceId={selectedTraceId}
                      appId={resolvedAppId}
                      initialTrace={selectedTrace}
                      activeTab={activeTab}
                      className="flex-1 min-h-0"
                      signalsPortalTarget={turnSignalsPortalTarget}
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {sessionView === 'agents' && (
        <div className="flex-1 min-h-0">
          <SessionAgentGraph
            sessionId={sessionId}
            appId={resolvedAppId}
            env={selectedEnvironment}
            sessionJwt={getJwt()}
          />
        </div>
      )}
    </div>
  );
}
